import React, { useEffect, useState } from 'react';
import styled, { ThemeProvider } from 'styled-components';
import {
  createPost,
  updatePost,
  deletePost,
  publishPost,
  uploadBlogImage,
  getBlogImageUrl,
  CATEGORIA_LABELS,
} from '../../services/blogService';
import { MF_BASE } from '../../basePath';
import MarkdownRenderer from '../../blog/components/MarkdownRenderer';

// ============ Themes ============
const lightTheme = {
  mode: 'light',
  bg: '#FAFAF7', surface: '#FFFFFF', surfaceAlt: '#F5F3EE',
  ink: '#1A1F2B', inkSoft: '#3D434F', muted: '#6B7280',
  line: '#E8E5DE', gold: '#B8935A', goldSoft: '#F2EBDD',
  ok: '#2F7D5B', warn: '#B8935A', danger: '#9A3B3B',
  shadow: 'rgba(0,0,0,.06)',
};
const darkTheme = {
  mode: 'dark',
  bg: '#0F1117', surface: '#181B23', surfaceAlt: '#1F2330',
  ink: '#F1ECE2', inkSoft: '#C9C4B8', muted: '#8A8F9A',
  line: '#262A36', gold: '#C9A06A', goldSoft: 'rgba(201,160,106,.14)',
  ok: '#5BB089', warn: '#C9A06A', danger: '#C5544A',
  shadow: 'rgba(0,0,0,.4)',
};
const THEME_KEY = 'mf_admin_v2_theme';

const EMPTY = {
  slug: '', titulo: '', subtitulo: '', resumo: '',
  conteudo_md: '', categoria: 'guia-bairro', tags: [],
  bairro_relacionado: '', imagem_capa: '',
  seo_title: '', seo_description: '', seo_keywords: [],
  status: 'rascunho', autor: 'Equipe MF Real Estate',
};

function slugify(s) {
  return (s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60);
}

// Stopwords pt-BR pra remoção em sugestões SEO
const STOP_KEYS = new Set([
  'a','o','as','os','e','é','de','da','do','das','dos','em','no','na','nos','nas',
  'um','uma','uns','umas','para','por','com','sem','que','se','sua','seu','suas','seus',
  'mas','ou','como','ao','aos','à','às','isso','isto','aquilo','este','esta','estes','estas',
  'esse','essa','esses','essas','já','também','tambem','sobre','até','ate','pelo','pela',
  'mais','menos','muito','muita','muitos','muitas','quem','onde','quando','porque','porquê',
]);

const CATEGORIA_KEYWORD_MAP = {
  'guia-bairro': ['guia de bairro', 'morar em', 'mercado imobiliário'],
  'financeiro': ['financiamento imóvel', 'investir imóvel', 'mercado imobiliário'],
  'investimento': ['investir em imóvel', 'valorização imóvel', 'rentabilidade imóvel'],
  'mercado': ['mercado imobiliário', 'tendências imóveis'],
  'processo': ['como comprar imóvel', 'documentação imóvel'],
};

function generateBlogKeywords(form) {
  const out = new Set();
  const bairro = (form.bairro_relacionado || '').trim();

  // 1) Palavras "importantes" do título (não-stopword, >3 chars)
  const titleWords = String(form.titulo || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9\s-]/g, ' ')
    .split(/\s+/)
    .filter(w => w.length > 3 && !STOP_KEYS.has(w));

  // Reconstroi a versão acentuada original a partir do título completo
  const titleAccented = String(form.titulo || '');
  const findInTitle = (word) => {
    const re = new RegExp('\\b' + word + '\\w*\\b', 'i');
    const m = titleAccented.normalize('NFD').replace(/[̀-ͯ]/g, '').match(re);
    if (m) {
      const idx = titleAccented.normalize('NFD').replace(/[̀-ͯ]/g, '').indexOf(m[0]);
      return titleAccented.slice(idx, idx + m[0].length);
    }
    return word;
  };

  // 2) Bigramas do título (pares de palavras seguidas, ambas não-stopword)
  const tokens = titleAccented.split(/\s+/).filter(Boolean);
  for (let i = 0; i < tokens.length - 1; i++) {
    const a = tokens[i].toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9-]/g, '');
    const b = tokens[i + 1].toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9-]/g, '');
    if (a.length > 3 && b.length > 3 && !STOP_KEYS.has(a) && !STOP_KEYS.has(b)) {
      out.add((tokens[i] + ' ' + tokens[i + 1]).toLowerCase().replace(/[,.;:!?]/g, ''));
    }
  }

  // 3) Bairro relacionado: combos com palavras-chave imobiliárias
  if (bairro) {
    const b = bairro.toLowerCase();
    out.add(b);
    out.add(`imóveis ${b}`);
    out.add(`morar em ${b}`);
    out.add(`apartamentos ${b}`);
  }

  // 4) Keywords por categoria
  const catKeys = CATEGORIA_KEYWORD_MAP[form.categoria] || [];
  for (const k of catKeys) {
    out.add(bairro ? `${k} ${bairro.toLowerCase()}` : k);
  }

  // 5) Palavras únicas importantes do título (singulares)
  for (const w of titleWords.slice(0, 5)) {
    out.add(findInTitle(w).toLowerCase());
  }

  // Limpa, dedup, capitaliza primeira letra
  return Array.from(out)
    .map(k => k.trim().replace(/\s+/g, ' '))
    .filter(k => k.length > 3 && k.length < 60)
    .slice(0, 12);
}

// ============ Styled ============
const Page = styled.div`
  min-height: 100vh;
  background: ${p => p.theme.bg};
  color: ${p => p.theme.ink};
  font-family: 'Inter', -apple-system, sans-serif;
  padding-bottom: 60px;
`;
const StickyHeader = styled.header`
  position: sticky; top: 0; z-index: 50;
  background: ${p => p.theme.surface};
  border-bottom: 1px solid ${p => p.theme.line};
  backdrop-filter: blur(8px);
`;
const HeaderTop = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  gap: 16px; padding: 16px 32px; flex-wrap: wrap;
  @media (max-width: 600px) { padding: 12px 16px; }
`;
const TitleBox = styled.div`
  display: flex; align-items: center; gap: 16px; flex: 1; min-width: 0;
  > div { flex: 1; min-width: 0; overflow: hidden; }
  h1 {
    font-family: 'Cormorant Garamond', Georgia, serif;
    font-size: 22px; font-weight: 600; margin: 0; color: ${p => p.theme.ink};
    line-height: 1.25;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .crumb {
    font-size: 11px; letter-spacing: 1.5px; text-transform: uppercase; color: ${p => p.theme.muted};
    margin-bottom: 4px;
  }
`;
const BackBtn = styled.button`
  border: 1px solid ${p => p.theme.line}; background: transparent;
  color: ${p => p.theme.inkSoft}; width: 38px; height: 38px;
  border-radius: 50%; cursor: pointer; font-size: 16px;
  display: flex; align-items: center; justify-content: center;
  &:hover { background: ${p => p.theme.surfaceAlt}; color: ${p => p.theme.ink}; }
`;
const Actions = styled.div`
  display: flex; gap: 8px; align-items: center; flex-wrap: wrap;
`;
const ActionBtn = styled.button`
  border: 1px solid ${p => p.theme.line};
  background: ${p => {
    if (p.$primary) return p.theme.ink;
    if (p.$danger) return 'transparent';
    return p.theme.surface;
  }};
  color: ${p => {
    if (p.$primary) return p.theme.bg;
    if (p.$danger) return p.theme.danger;
    return p.theme.ink;
  }};
  ${p => p.$danger && `border-color: ${p.theme.danger};`}
  padding: 9px 18px; border-radius: 6px;
  font-size: 13px; font-weight: 500; letter-spacing: 0.3px;
  cursor: pointer; font-family: inherit; transition: all .15s;
  &:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 2px 8px ${p => p.theme.shadow}; }
  &:disabled { opacity: .5; cursor: not-allowed; }
`;
const ThemeToggle = styled.button`
  border: 1px solid ${p => p.theme.line};
  background: ${p => p.theme.surface};
  color: ${p => p.theme.ink};
  width: 38px; height: 38px; border-radius: 50%;
  cursor: pointer; font-size: 14px;
  display: flex; align-items: center; justify-content: center;
`;
const Tabs = styled.div`
  display: flex; gap: 0; padding: 0 32px;
  overflow-x: auto;
  border-top: 1px solid ${p => p.theme.line};
  background: ${p => p.theme.surface};
  &::-webkit-scrollbar { height: 0; }
  @media (max-width: 600px) { padding: 0 12px; }
`;
const Tab = styled.button`
  border: none; background: transparent;
  color: ${p => p.$active ? p.theme.ink : p.theme.muted};
  padding: 14px 20px; font-size: 13px;
  font-weight: ${p => p.$active ? 600 : 500};
  letter-spacing: 0.3px; cursor: pointer; font-family: inherit;
  border-bottom: 2px solid ${p => p.$active ? p.theme.gold : 'transparent'};
  white-space: nowrap;
  &:hover { color: ${p => p.theme.ink}; }
`;
const Body = styled.main`
  max-width: 1280px; margin: 0 auto; padding: 32px;
  @media (max-width: 600px) { padding: 20px 14px; }
`;
const Section = styled.section`
  background: ${p => p.theme.surface};
  border: 1px solid ${p => p.theme.line};
  border-radius: 10px;
  padding: 24px 28px; margin-bottom: 18px;
  @media (max-width: 600px) { padding: 18px 16px; }
`;
const SecTitle = styled.h2`
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: 22px; font-weight: 600;
  color: ${p => p.theme.ink}; margin: 0 0 4px;
  &::after {
    content: ''; display: block; width: 30px; height: 2px;
    background: ${p => p.theme.gold}; margin-top: 6px;
  }
`;
const SecHint = styled.p`
  font-size: 12px; color: ${p => p.theme.muted}; margin: 0 0 18px; letter-spacing: 0.2px;
`;
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(${p => p.$cols || 2}, 1fr);
  gap: 16px;
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;
const Field = styled.label`
  display: flex; flex-direction: column; gap: 6px;
  ${p => p.$full && 'grid-column: 1 / -1;'}
  font-size: 10px; letter-spacing: 1.5px;
  text-transform: uppercase; color: ${p => p.theme.muted}; font-weight: 500;
  input, select, textarea {
    background: ${p => p.theme.bg};
    border: 1px solid ${p => p.theme.line};
    color: ${p => p.theme.ink};
    border-radius: 7px; padding: 10px 12px;
    font-size: 14px; font-family: inherit;
    letter-spacing: 0.2px; text-transform: none; font-weight: 400;
    outline: none; transition: border-color .15s;
    &:focus { border-color: ${p => p.theme.gold}; box-shadow: 0 0 0 3px ${p => p.theme.goldSoft}; }
  }
  textarea { resize: vertical; min-height: 60px; }
  .hint {
    font-size: 11px; letter-spacing: 0; text-transform: none; font-weight: 400;
    color: ${p => p.theme.muted}; margin-top: 2px;
  }
  .counter { font-size: 11px; letter-spacing: 0; text-transform: none; }
  .counter.over { color: ${p => p.theme.danger}; }
`;
const SlugRow = styled.div`
  display: flex; gap: 6px;
  input { flex: 1; font-family: 'SF Mono', monospace; font-size: 13px; }
  button {
    border: 1px solid ${p => p.theme.line};
    background: ${p => p.theme.surface};
    color: ${p => p.theme.ink};
    padding: 0 14px; border-radius: 6px;
    cursor: pointer; font-family: inherit;
    &:hover { background: ${p => p.theme.surfaceAlt}; }
  }
`;
const MdSplit = styled.div`
  display: grid;
  grid-template-columns: ${p => p.$preview ? '1fr 1fr' : '1fr'};
  gap: 14px;
  @media (max-width: 980px) { grid-template-columns: 1fr; }
`;
const MdEditor = styled.textarea`
  font-family: 'SF Mono', Menlo, monospace;
  font-size: 14px; line-height: 1.6;
  background: ${p => p.theme.bg};
  border: 1px solid ${p => p.theme.line};
  color: ${p => p.theme.ink};
  border-radius: 8px; padding: 14px 16px;
  resize: vertical; min-height: 500px;
  outline: none;
  &:focus { border-color: ${p => p.theme.gold}; }
`;
const MdPreview = styled.div`
  background: ${p => p.theme.bg};
  border: 1px solid ${p => p.theme.line};
  border-radius: 8px; padding: 20px 24px;
  overflow: auto; max-height: 700px;
  font-size: 15px; line-height: 1.7;
  color: ${p => p.theme.ink};
  h1, h2, h3 {
    font-family: 'Cormorant Garamond', Georgia, serif;
    color: ${p => p.theme.ink}; margin: 12px 0 6px;
  }
  h2 { font-size: 24px; }
  h3 { font-size: 19px; }
  p { margin: 0 0 10px; }
  a { color: ${p => p.theme.gold}; }
  ul, ol { padding-left: 22px; margin: 6px 0 12px; }
  blockquote {
    border-left: 3px solid ${p => p.theme.gold};
    padding-left: 12px; margin: 8px 0;
    color: ${p => p.theme.muted}; font-style: italic;
  }
`;
const CoverPreview = styled.div`
  width: 280px; height: 175px;
  border-radius: 8px;
  background: ${p => p.theme.surfaceAlt};
  border: 1px solid ${p => p.theme.line};
  background-size: cover; background-position: center;
  background-image: ${p => p.$src ? `url(${p.$src})` : 'none'};
  display: flex; align-items: center; justify-content: center;
  color: ${p => p.theme.muted};
  font-size: 11px; letter-spacing: 1px; text-transform: uppercase;
`;
const KeywordChip = styled.span`
  display: inline-flex; align-items: center; gap: 6px;
  background: ${p => p.theme.goldSoft};
  color: ${p => p.theme.ink};
  padding: 4px 10px; border-radius: 999px;
  font-size: 12px; border: 1px solid ${p => p.theme.gold};
  button {
    border: none; background: transparent;
    color: ${p => p.theme.inkSoft};
    cursor: pointer; font-size: 14px;
    line-height: 1; padding: 0;
    &:hover { color: ${p => p.theme.danger}; }
  }
`;
const AgentCard = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 14px;
  .stat {
    background: ${p => p.theme.bg};
    border: 1px solid ${p => p.theme.line};
    border-radius: 10px;
    padding: 16px;
  }
  .stat .l { font-size: 10px; letter-spacing: 1.5px; color: ${p => p.theme.muted}; text-transform: uppercase; }
  .stat .v {
    font-family: 'Cormorant Garamond', Georgia, serif;
    font-size: 28px; font-weight: 600; margin-top: 6px;
    color: ${p => p.theme.ink};
  }
  .stat.ok .v { color: ${p => p.theme.ok}; }
  .stat.warn .v { color: ${p => p.theme.warn}; }
`;
const Saved = styled.span`
  font-size: 12px; color: ${p => p.theme.ok}; letter-spacing: 0.5px;
`;

// ============ Component ============
const TABS = [
  { id: 'conteudo', label: 'Conteúdo' },
  { id: 'metadata', label: 'Metadata' },
  { id: 'seo', label: 'SEO' },
];

export default function BlogFormV2({ postId, onBack, onSaved }) {
  const [tab, setTab] = useState('conteudo');
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(!!postId);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [keywordInput, setKeywordInput] = useState('');
  const [editId, setEditId] = useState(postId || null);
  const [mode] = useState(() => {
    if (typeof window === 'undefined') return 'light';
    return localStorage.getItem(THEME_KEY) === 'dark' ? 'dark' : 'light';
  });
  const theme = mode === 'dark' ? darkTheme : lightTheme;

  // Load existing post
  useEffect(() => {
    if (!postId) return;
    setLoading(true);
    (async () => {
      const { supabase } = await import('../../../../services/supabaseClient');
      const { data } = await supabase.from('mf_blog_posts').select('*').eq('id', postId).single();
      if (data) {
        setForm({
          ...EMPTY,
          ...data,
          seo_keywords: Array.isArray(data.seo_keywords) ? data.seo_keywords : [],
          tags: Array.isArray(data.tags) ? data.tags : [],
        });
      }
      setLoading(false);
    })();
  }, [postId]);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const generateSlug = () => setForm(f => ({ ...f, slug: slugify(f.titulo) }));

  const addKeyword = () => {
    const k = keywordInput.trim();
    if (!k) return;
    if (form.seo_keywords.includes(k)) return;
    setForm(f => ({ ...f, seo_keywords: [...f.seo_keywords, k] }));
    setKeywordInput('');
  };
  const removeKeyword = (i) => {
    setForm(f => ({ ...f, seo_keywords: f.seo_keywords.filter((_, idx) => idx !== i) }));
  };

  const handleUploadCapa = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const path = await uploadBlogImage(file, 'capas');
      if (path) setForm(f => ({ ...f, imagem_capa: path }));
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!form.titulo) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        slug: form.slug || slugify(form.titulo),
      };
      delete payload.id;
      delete payload.created_at;
      if (editId) {
        await updatePost(editId, payload);
      } else {
        const created = await createPost(payload);
        if (created?.id) setEditId(created.id);
      }
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1500);
      if (onSaved) onSaved();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editId) return;
    if (!window.confirm('Excluir este artigo permanentemente?')) return;
    await deletePost(editId);
    onBack();
  };

  const handleTogglePublish = async () => {
    if (!editId) return;
    if (form.status === 'publicado') {
      await updatePost(editId, { status: 'rascunho' });
      setForm(f => ({ ...f, status: 'rascunho' }));
    } else {
      await publishPost(editId);
      setForm(f => ({ ...f, status: 'publicado' }));
    }
  };

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <Page><Body style={{ textAlign: 'center', padding: 80 }}>Carregando…</Body></Page>
      </ThemeProvider>
    );
  }

  const coverUrl = form.imagem_capa
    ? (form.imagem_capa.startsWith('http') ? form.imagem_capa : getBlogImageUrl(form.imagem_capa))
    : '';

  const wordCount = (form.conteudo_md || '').split(/\s+/).filter(Boolean).length;
  const titleOver = (form.titulo || '').length > 70;
  const resumoOver = (form.resumo || '').length > 160;
  const seoTitleOver = (form.seo_title || '').length > 60;
  const seoDescOver = (form.seo_description || '').length > 155;
  const isAgentPost = form.gerado_por === 'agente';

  return (
    <ThemeProvider theme={theme}>
      <Page>
        <StickyHeader>
          <HeaderTop>
            <TitleBox>
              <BackBtn onClick={onBack} title="Voltar">←</BackBtn>
              <div>
                <div className="crumb">Blog · {editId ? 'Editar' : 'Novo artigo'}</div>
                <h1 title={form.titulo || ''}>
                  {(() => {
                    const t = form.titulo || (editId ? 'Editar artigo' : 'Novo artigo');
                    return t.length > 48 ? t.slice(0, 48).trimEnd() + '…' : t;
                  })()}
                </h1>
              </div>
            </TitleBox>
            <Actions>
              {savedFlash && <Saved>Salvo!</Saved>}
              <ThemeToggle title="Toggle tema via sidebar/dashboard">{mode === 'dark' ? '☀' : '☾'}</ThemeToggle>
              {editId && form.slug && (
                <ActionBtn as="a" href={`${MF_BASE}/blog/${form.slug}`} target="_blank" rel="noopener noreferrer">Preview no site</ActionBtn>
              )}
              <ActionBtn onClick={() => setShowPreview(v => !v)}>
                {showPreview ? 'Ocultar preview' : 'Mostrar preview'}
              </ActionBtn>
              {editId && (
                <ActionBtn onClick={handleTogglePublish}>
                  {form.status === 'publicado' ? 'Despublicar' : 'Publicar'}
                </ActionBtn>
              )}
              {editId && (
                <ActionBtn $danger onClick={handleDelete}>Excluir</ActionBtn>
              )}
              <ActionBtn $primary onClick={handleSave} disabled={saving || !form.titulo}>
                {saving ? 'Salvando…' : 'Salvar'}
              </ActionBtn>
            </Actions>
          </HeaderTop>
          <Tabs>
            {TABS.map(t => (
              <Tab key={t.id} $active={tab === t.id} onClick={() => setTab(t.id)}>{t.label}</Tab>
            ))}
            {isAgentPost && (
              <Tab $active={tab === 'agente'} onClick={() => setTab('agente')}>🤖 Agente IA</Tab>
            )}
          </Tabs>
        </StickyHeader>

        <Body>
          {tab === 'conteudo' && (
            <>
              <Section>
                <SecTitle>Informações</SecTitle>
                <SecHint>Título, resumo e configurações básicas do artigo.</SecHint>
                <Grid $cols={2}>
                  <Field $full>
                    Título
                    {titleOver && <span className="counter over"> · {(form.titulo || '').length}/70</span>}
                    <input value={form.titulo} onChange={set('titulo')} onBlur={() => !form.slug && generateSlug()} placeholder="Ex: Vale a pena morar no Alphaville Residencial 08?" />
                  </Field>
                  <Field $full>
                    Subtítulo
                    <input value={form.subtitulo} onChange={set('subtitulo')} placeholder="Análise honesta de um dos condomínios mais consolidados em 2026" />
                  </Field>
                  <Field $full>
                    Resumo <span className={`counter ${resumoOver ? 'over' : ''}`}>· {(form.resumo || '').length}/160</span>
                    <textarea value={form.resumo} onChange={set('resumo')} rows={2} maxLength={200} placeholder="Vira meta description e card de listagem" />
                  </Field>
                </Grid>
              </Section>

              <Section>
                <SecTitle>Conteúdo (Markdown)</SecTitle>
                <SecHint>
                  Use <b>##</b> para subtítulos, <b>**</b> para negrito, <b>*</b> para itálico, <b>[texto](url)</b> para links · {wordCount} palavras
                </SecHint>
                <MdSplit $preview={showPreview}>
                  <MdEditor
                    value={form.conteudo_md}
                    onChange={set('conteudo_md')}
                    rows={28}
                    placeholder={'## Por que esse condomínio se destaca\n\nNo nosso portfólio atual…\n\n## Quanto custa morar aqui hoje\n\n…\n\n## FAQ\n\n### Pergunta?\n\nResposta.'}
                  />
                  {showPreview && (
                    <MdPreview>
                      <MarkdownRenderer>{form.conteudo_md}</MarkdownRenderer>
                    </MdPreview>
                  )}
                </MdSplit>
              </Section>
            </>
          )}

          {tab === 'metadata' && (
            <>
              <Section>
                <SecTitle>Metadata</SecTitle>
                <SecHint>Categoria, slug, autor, status e relacionamento com bairro.</SecHint>
                <Grid $cols={2}>
                  <Field $full>
                    Slug (URL)
                    <SlugRow>
                      <input value={form.slug} onChange={set('slug')} placeholder="auto-gerado do título" />
                      <button type="button" onClick={generateSlug} title="Gerar do título">↻</button>
                    </SlugRow>
                    <span className="hint">{MF_BASE}/blog/{form.slug || 'seu-slug'}</span>
                  </Field>
                  <Field>
                    Categoria
                    <select value={form.categoria} onChange={set('categoria')}>
                      {Object.entries(CATEGORIA_LABELS).map(([slug, label]) => (
                        <option key={slug} value={slug}>{label}</option>
                      ))}
                    </select>
                  </Field>
                  <Field>
                    Status
                    <select value={form.status} onChange={set('status')}>
                      <option value="rascunho">Rascunho</option>
                      <option value="revisao">Em revisão</option>
                      <option value="publicado">Publicado</option>
                    </select>
                  </Field>
                  <Field>
                    Autor
                    <input value={form.autor} onChange={set('autor')} />
                  </Field>
                  <Field>
                    Bairro relacionado
                    <input value={form.bairro_relacionado} onChange={set('bairro_relacionado')} placeholder="Ex: Alphaville Residencial 08" />
                    <span className="hint">Se bater com bairro de algum imóvel publicado, aparece "Imóveis em…" no rodapé.</span>
                  </Field>
                </Grid>
              </Section>

              <Section>
                <SecTitle>Imagem de capa</SecTitle>
                <SecHint>JPG/PNG/WebP. Recomenda 1200×630px (proporção 16:9).</SecHint>
                <div style={{ display: 'flex', gap: 18, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  <CoverPreview $src={coverUrl}>{!coverUrl && 'sem imagem'}</CoverPreview>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10, flex: 1, minWidth: 220 }}>
                    <input type="file" accept="image/*" onChange={handleUploadCapa} disabled={uploading} />
                    {uploading && <span style={{ fontSize: 12, color: theme.muted }}>Subindo…</span>}
                    {form.imagem_capa && (
                      <ActionBtn type="button" onClick={() => setForm(f => ({ ...f, imagem_capa: '' }))} style={{ alignSelf: 'flex-start' }}>
                        Remover
                      </ActionBtn>
                    )}
                    <Field>
                      Ou cole uma URL
                      <input
                        type="text"
                        value={form.imagem_capa}
                        onChange={set('imagem_capa')}
                        placeholder="https://… ou caminho dentro do bucket"
                        style={{ fontFamily: 'monospace', fontSize: 12 }}
                      />
                    </Field>
                  </div>
                </div>
              </Section>
            </>
          )}

          {tab === 'seo' && (
            <Section>
              <SecTitle>SEO</SecTitle>
              <SecHint>Como o artigo aparece no Google e redes sociais. Auto-gerado do título/resumo quando vazio.</SecHint>
              <Grid $cols={1}>
                <Field>
                  SEO Title <span className={`counter ${seoTitleOver ? 'over' : ''}`}>· {(form.seo_title || '').length}/60</span>
                  <input value={form.seo_title} onChange={set('seo_title')} maxLength={70} placeholder={form.titulo || 'Auto-gerado do título se vazio'} />
                </Field>
                <Field>
                  Meta description <span className={`counter ${seoDescOver ? 'over' : ''}`}>· {(form.seo_description || '').length}/155</span>
                  <textarea value={form.seo_description} onChange={set('seo_description')} rows={2} maxLength={200} placeholder={form.resumo || 'Auto-gerado do resumo se vazio'} />
                </Field>
                <Field>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, letterSpacing: 0, textTransform: 'none' }}>
                    <span style={{ fontSize: 10, letterSpacing: 1.5, textTransform: 'uppercase' }}>Palavras-chave</span>
                    <button
                      type="button"
                      onClick={() => {
                        const sugeridas = generateBlogKeywords(form);
                        const novas = sugeridas.filter(k => !form.seo_keywords.includes(k));
                        if (novas.length === 0) return;
                        setForm(f => ({ ...f, seo_keywords: [...f.seo_keywords, ...novas] }));
                      }}
                      style={{
                        background: 'transparent',
                        border: '1px solid currentColor',
                        color: '#B8935A',
                        padding: '4px 10px',
                        borderRadius: 6,
                        fontSize: 11,
                        letterSpacing: 0.5,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        fontWeight: 500,
                        textTransform: 'none',
                      }}
                      title="Sugere palavras-chave baseadas no título, bairro e categoria"
                    >
                      ✨ Sugerir keywords
                    </button>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8, marginTop: 6 }}>
                    {form.seo_keywords.map((k, i) => (
                      <KeywordChip key={i}>
                        {k}
                        <button type="button" onClick={() => removeKeyword(i)}>×</button>
                      </KeywordChip>
                    ))}
                    {form.seo_keywords.length === 0 && <span className="hint">Nenhuma palavra-chave. Clique em "Sugerir keywords" ou digite abaixo.</span>}
                  </div>
                  <SlugRow>
                    <input
                      value={keywordInput}
                      onChange={(e) => setKeywordInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addKeyword())}
                      placeholder="Adicionar palavra-chave…"
                    />
                    <button type="button" onClick={addKeyword}>+</button>
                  </SlugRow>
                </Field>
              </Grid>
            </Section>
          )}

          {tab === 'agente' && isAgentPost && (
            <>
              <Section>
                <SecTitle>Gerado pelo Agente IA</SecTitle>
                <SecHint>Métricas e fontes da geração automática.</SecHint>
                <AgentCard>
                  <div className={`stat ${(form.qualidade_score || 0) >= 70 ? 'ok' : 'warn'}`}>
                    <div className="l">Qualidade</div>
                    <div className="v">{form.qualidade_score ?? '—'}<span style={{ fontSize: 14, color: theme.muted }}>/100</span></div>
                  </div>
                  <div className="stat">
                    <div className="l">Custo</div>
                    <div className="v">${((form.custo_geracao_cents || 0) / 100).toFixed(3)}</div>
                  </div>
                  <div className="stat">
                    <div className="l">Veredito</div>
                    <div className="v" style={{ fontSize: 17, fontFamily: 'inherit', textTransform: 'capitalize' }}>
                      {form.qualidade_notas?.veredito || '—'}
                    </div>
                  </div>
                </AgentCard>
              </Section>

              {form.qualidade_notas?.scores_individuais && (
                <Section>
                  <SecTitle>Scores E-E-A-T</SecTitle>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {Object.entries(form.qualidade_notas.scores_individuais).map(([k, v]) => (
                      <KeywordChip key={k}><b>{k.replace(/_/g, ' ')}:</b> {v}/20</KeywordChip>
                    ))}
                  </div>
                </Section>
              )}

              {form.qualidade_notas?.sugestoes_melhoria?.length > 0 && (
                <Section>
                  <SecTitle>Sugestões do quality gate</SecTitle>
                  <ul style={{ margin: 0, paddingLeft: 22, fontSize: 14, color: theme.inkSoft, lineHeight: 1.6 }}>
                    {form.qualidade_notas.sugestoes_melhoria.map((s, i) => <li key={i}>{s}</li>)}
                  </ul>
                </Section>
              )}

              {form.fontes_pesquisa?.fatos_web?.length > 0 && (
                <Section>
                  <SecTitle>Fontes da pesquisa</SecTitle>
                  <ul style={{ margin: 0, paddingLeft: 22, fontSize: 13, color: theme.inkSoft, lineHeight: 1.6 }}>
                    {form.fontes_pesquisa.fatos_web.map((f, i) => (
                      <li key={i}>
                        {f.fato}{' '}
                        {f.fonte && <a href={f.fonte} target="_blank" rel="noopener noreferrer" style={{ color: theme.gold }}>[fonte]</a>}
                      </li>
                    ))}
                  </ul>
                </Section>
              )}
            </>
          )}
        </Body>
      </Page>
    </ThemeProvider>
  );
}
